import React, { FunctionComponent, MouseEventHandler } from "react";

import { bemClassNames, createQA, transformKeysToParamCase } from "@multiweb/reactUtils";
import { AccessibilityProps, KeyboardNavigationProps, QAProps, TabIndex } from "@multiweb/types/react";
import { ComponentTheme } from "@multiweb/uiShared/ComponentTheme";
import { FontWeight, TextColor, WhiteSpace } from "./enums";
import { getAriaLabel, getTabIndex } from "./utils";

export type TextProps = {
    /** Příznak, zda je text vizuálně neaktivní - výchozí hodnota je false */
    disabled?: boolean;
    /** Zobrazený text - výchozí hodnota je prázdný řetězec */
    value?: string;
    /** Tloušťka písma */
    fontWeight?: FontWeight;
    /** Barevné téma */
    theme?: ComponentTheme;
    /** Popisek */
    title?: string;
    /** Barva textu - má vyšší prioritu než barevné téma */
    color?: TextColor;
    /** Příznak, zda je možné označit text */
    isTextSelectable?: boolean;
    /** Způsob zpracování bílých znaků uvnitř prvku */
    whiteSpace?: WhiteSpace;
    /** Příznak, zda se má text ořezávat. */
    ellipsis?: boolean;
    /** Příznak, zda má být text podtržený. */
    underline?: boolean;
    /** Akce, která se provede při obyčejném kliku na komponentu. */
    onClick?: MouseEventHandler;
    /** Umožňuje nastavit tabindex. Pokud není zadaný, určuje se výsledná hodnota tabIndexu na základě onKeyDown handleru. Pokud je handler zadaný, použije se hodnota TAB_INDEX_FOCUSABLE_ONLY.
     * Pokud neexistuje, použije se hodnota undefined a chování tedy závisí na prohlížeči. */
    tabIndex?: TabIndex;
    /** Popisek pole, který se použije jako aria-label pokud není zadané ariaLabelledBy. Pokud není explicitně zadané ariaLabelledBy ani ariaLabel, použije se v aria-label title. */
    ariaLabel?: string;
} & QAProps & Pick<AccessibilityProps, "role" | "ariaSort" | "hideOutlineOnFocus" | "ariaLabelledBy"> & KeyboardNavigationProps;

const classNames = bemClassNames("text-component");

/** Základní textová komponenta */
export const Text: FunctionComponent<TextProps> = ({
    value = "",
    fontWeight,
    theme = ComponentTheme.default,
    title,
    qa,
    disabled = false,
    color = TextColor.inherit,
    isTextSelectable = true,
    whiteSpace,
    ellipsis,
    underline,
    role,
    ariaSort,
    hideOutlineOnFocus,
    onClick,
    onKeyDown,
    onKeyUp,
    onKeyPress,
    tabIndex,
    ariaLabelledBy,
    ariaLabel,
}) => (
    <span
        className={classNames(transformKeysToParamCase({ theme, fontWeight, disabled, color, selectableText: isTextSelectable, ellipsis, underline, hideOutlineOnFocus }))}
        style={{ whiteSpace }}
        aria-label={getAriaLabel(ariaLabelledBy, ariaLabel, title)}
        aria-labelledby={ariaLabelledBy}
        aria-sort={ariaSort ?? undefined}
        aria-disabled={disabled || undefined}
        tabIndex={getTabIndex(tabIndex, onKeyDown, onKeyUp, onKeyPress)}
        {...createQA(qa)}
        {...{ title, onKeyDown, onKeyUp, onKeyPress, onClick, role }}
    >
        {value}
    </span>
);
